import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { LastHorizonKeyDates } from "@/components/LastHorizonKeyDates";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import AuthForms from "@/pages/membership/AuthForms";
import LastHorizonWizard from "@/pages/lasthorizon/LastHorizonWizard";

function AlreadyPaidScreen({ app }) {
  return (
    <div data-testid="last-horizon-already-paid" className="bg-white border border-ptf-primary/12 p-8 md:p-12 max-w-xl">
      <CheckCircle2 className="h-10 w-10 text-emerald-600" />
      <p className="mt-4 text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50">The Last Horizon</p>
      <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">Already Paid</h2>
      <p className="mt-2 text-sm text-ptf-text/70">Amount Payable: <strong>₹0</strong></p>
      <p className="mt-5 text-sm text-ptf-text/70 leading-relaxed">
        Your Wildlife Filmmaking Challenge entry has been received for jury consideration. Status:{" "}
        <span className="font-semibold text-ptf-primary" data-testid="last-horizon-status">{app.status}</span>.
        You do not need to apply or pay again.
      </p>
      <PtfButton to="/account" className="mt-6">Go to My Dashboard</PtfButton>
    </div>
  );
}

export default function LastHorizonApply() {
  const { member, ready } = useMemberAuth();
  const queryClient = useQueryClient();
  const { data: app, isLoading } = useQuery({
    queryKey: ["last-horizon-application"],
    queryFn: async () => (await api.get("/member/last-horizon/application")).data,
    enabled: !!member,
  });

  return (
    <>
      <Seo title="Apply for The Last Horizon" path="/last-horizon/apply" description="Enter the Wildlife Filmmaking Challenge: submit ten wildlife photographs and a 30-second video for jury consideration." />
      <PageHero
        eyebrow="The Last Horizon"
        titleLines={["Apply for", "The Last Horizon"]}
        intro="Ten wildlife photographs. One 30-second video. Six filmmakers selected by the Foundation jury."
        image={IMAGES.forest}
      />
      <section className="mx-auto max-w-[900px] px-5 sm:px-8 py-16 md:py-24">
        <div className="mb-10"><LastHorizonKeyDates /></div>
        <div className="mb-10 border border-ptf-primary/12 bg-ptf-primary/[0.03] p-5 text-xs text-ptf-text/65 leading-relaxed space-y-2" data-testid="last-horizon-membership-info">
          <p>Successful applicants who do not already have an active Planet Trotter Foundation membership receive <strong>1 year of Foundation membership</strong>, including Learning &amp; Training access and a member discount on eligible training programmes.</p>
          <p>If you already have an active membership, you pay only the ₹5,000 Last Horizon Application &amp; Community Access Fee. Your existing membership stays valid according to its current period, with no duplicate charge. This benefit is individual and non-transferable.</p>
          <p>
            Please read the official{" "}
            <Link to="/last-horizon/terms" target="_blank" rel="noopener noreferrer" data-testid="last-horizon-terms-link-apply" className="underline font-semibold text-ptf-secondary hover:text-ptf-primary">
              Terms &amp; Conditions
            </Link>{" "}
            before applying.
          </p>
        </div>
        {!ready || (member && isLoading) ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-ptf-primary" /></div>
        ) : !member ? (
          <div data-testid="last-horizon-auth-gate">
            <p className="text-sm text-ptf-text/70 mb-6">Sign in or create a free Planet Trotter Foundation account to start your application.</p>
            <AuthForms />
          </div>
        ) : !app ? (
          <div className="text-sm text-ptf-text/60">Could not load your application. Please refresh the page or sign in again.</div>
        ) : app.status === "Draft" || app.status === "Paid" ? (
          <LastHorizonWizard app={app} onUpdated={() => queryClient.invalidateQueries({ queryKey: ["last-horizon-application"] })} />
        ) : (
          <AlreadyPaidScreen app={app} />
        )}
      </section>
    </>
  );
}
